// What is safe to put in telemetry from a launch URL.
//
// Lives apart from utils/telemetry so bugsnag-client can use it without an
// import cycle.

/**
 * Params logged by value. Everything else — credentials, PII, and any param an
 * EMR adds later — is reduced to a presence boolean, so withholding is the
 * default for anything unrecognised.
 */
export const LOGGABLE_PARAMS = [
  "iss",
  "jwks_uri",
  "emr_patient_id",
  "emr_hospital_id",
  "emr_user_id",
  "fhir_patient_id",
  "emr_user_role",
  "emr_user_service_area",
  "emr_user_dept_speciality",
];

const MAX_LOGGED_VALUE_LENGTH = 200;
const REDACTED = "[REDACTED]";

const isLoggable = (key: string): boolean =>
  LOGGABLE_PARAMS.includes(key.toLowerCase());

const truncate = (value: string): string =>
  value.length > MAX_LOGGED_VALUE_LENGTH
    ? `${value.slice(0, MAX_LOGGED_VALUE_LENGTH)}…`
    : value;

/** The query string for logging: allowlisted params by value, the rest as booleans. */
export const describeLaunchParams = (): Record<string, string | boolean> => {
  const described: Record<string, string | boolean> = {};
  new URLSearchParams(window.location.search).forEach((value, key) => {
    described[key] = isLoggable(key) ? truncate(value) : Boolean(value);
  });
  return described;
};

/**
 * Redact credentials from a URL the SDKs captured on their own.
 *
 * RUM records the view URL and Bugsnag the request URL, both before fhirclient
 * strips the query string — so `?launch=`, `?code=` and `?session_id=` reach
 * them verbatim unless scrubbed here.
 */
export const scrubUrl = (url?: string): string | undefined => {
  if (!url) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const keys: string[] = [];
    parsed.searchParams.forEach((_value, key) => keys.push(key));
    keys.forEach((key) => {
      if (!isLoggable(key)) parsed.searchParams.set(key, REDACTED);
    });
    // An implicit-flow access_token arrives in the fragment.
    parsed.hash = "";
    return parsed.toString();
  } catch {
    // Never let a URL we could not parse through unredacted.
    return REDACTED;
  }
};
